import React, { useEffect, useState } from "react"
import { Modal, Form, Input, Button, Radio, message, Divider, Steps } from "antd"
import DOBPicker from "./DOBPicker"
import moment, { Moment } from "moment-timezone"
import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons"

type AddSwimmerValues = { firstName: string; middleInitial?: string; lastName: string; gender: "Boy" | "Girl"; birthdate: Moment | null }

export function AddSwimmerModal({
  visible,
  onClose,
  onAdd,
  initialValues,
}: {
  visible: boolean
  onClose: () => void
  onAdd: (vals: AddSwimmerValues) => Promise<void> | void
  initialValues?: Partial<AddSwimmerValues>
}) {
  const [form] = Form.useForm<AddSwimmerValues>()

  const handleOk = async () => {
    try {
      const vals = await form.validateFields()
      await Promise.resolve(onAdd(vals))
      form.resetFields()
      onClose()
    } catch (_e) {}
  }

  useEffect(() => {
    if (!visible) form.resetFields()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  return (
    <Modal
      title="Add a Swimmer"
      open={visible}
      centered
      mask={{ closable: false }}
      onCancel={() => {
        form.resetFields()
        onClose()
      }}
      footer={[
        <Button key="cancel" onClick={() => { form.resetFields(); onClose() }}>Cancel</Button>,
        <Button key="submit" type="primary" onClick={handleOk}>Add Swimmer</Button>,
      ]}
    >
      <p style={{ color: "var(--darkgrey)" }}>Please enter your swimmer's name, middle initial, date of birth, and sex below.</p>
      <Form form={form} layout="vertical" preserve={false} initialValues={{ firstName: "", middleInitial: "", lastName: "", gender: "", birthdate: null, ...initialValues }}>
        <Form.Item name="firstName" rules={[{ required: true, message: "Please enter first name" }]}><Input placeholder="First name..." className="swimmer-name-input" /></Form.Item>
        <Form.Item name="middleInitial" rules={[{ pattern: /^[a-zA-Z]?$/, message: "Single letter" }]}><Input placeholder="M" maxLength={1} className="swimmer-name-input" /></Form.Item>
        <Form.Item name="lastName" rules={[{ required: true, message: "Please enter last name" }]}><Input placeholder="Last name..." className="swimmer-name-input" /></Form.Item>
        <Form.Item name="birthdate" label="Date of Birth" rules={[{ required: true, message: "Please pick birthdate" }]}><DOBPicker value={initialValues?.birthdate ?? null} onChange={(d) => form.setFieldValue("birthdate", d)} /></Form.Item>
        <Form.Item name="gender" label="Sex" rules={[{ required: true, message: "Please choose sex" }]}><Radio.Group aria-label="Sex" optionType="default" buttonStyle="solid"><Radio value="Boy">Boy</Radio><Radio value="Girl">Girl</Radio></Radio.Group></Form.Item>
      </Form>
    </Modal>
  )
}

export function MergeSwimmersModal({
  visible,
  onClose,
  swimmersToMerge,
  usersSwimmers,
  onMerge,
}: {
  visible: boolean
  onClose: () => void
  swimmersToMerge: number[]
  usersSwimmers: any[]
  onMerge: (payload: { swimmerIds: number[]; firstName: string; middleInitial?: string; lastName: string; gender: "Boy" | "Girl"; birthdate: Moment | null }) => Promise<void> | void
}) {
  const [form] = Form.useForm()
  const [localSelected, setLocalSelected] = useState<number[]>([])
  const [current, setCurrent] = useState<number>(0)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [previewVals, setPreviewVals] = useState<AddSwimmerValues | null>(null)
  const [confirmLoading, setConfirmLoading] = useState(false)

  // initialize when modal opens or prop changes
  useEffect(() => {
    if (visible) {
      setLocalSelected(swimmersToMerge ?? [])
      setCurrent(0)
      form.resetFields()
      setShowConfirmModal(false)
      setPreviewVals(null)
      setConfirmLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, swimmersToMerge])

  // prefill when entering step 2 or when selected changes
  useEffect(() => {
    if (current === 1 && localSelected.length > 0) {
      const first = usersSwimmers.find((u: any) => u.swimmer?.swimmerId === localSelected[0])
      if (first) {
        form.setFieldsValue({
          firstName: first.swimmer.firstName ?? "",
          middleInitial: first.swimmer.middleInitials ?? "",
          lastName: first.swimmer.lastName ?? "",
          gender: (first.swimmer.gender === "1" || first.swimmer.gender === 1) ? "Boy" : (first.swimmer.gender === "2" || first.swimmer.gender === 2 ? "Girl" : undefined),
          birthdate: first.swimmer.dateOfBirth ? moment(first.swimmer.dateOfBirth) : null,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, localSelected, usersSwimmers])

  const toggleSelection = (id?: number) => {
    if (id === undefined) return
    setLocalSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
  }

  const goNext = () => {
    if (localSelected.length <= 1) {
      message.warning("Select two or more swimmers to proceed.")
      return
    }
    setCurrent(1)
  }

  const goBack = () => setCurrent(0)

  // validate form and open final confirmation modal (preview)
  const onPrepareMerge = async () => {
    if (localSelected.length <= 1) {
      message.warning("Select two or more swimmers to proceed.")
      return
    }
    try {
      const vals = await form.validateFields()
      // store validated values for preview/confirm
      setPreviewVals(vals as AddSwimmerValues)
      setShowConfirmModal(true)
    } catch (_e) {
      // form validation will show errors
    }
  }

  const handleConfirmMerge = async () => {
    if (!previewVals) {
      message.error("No merged swimmer data available.")
      return
    }
    setConfirmLoading(true)
    try {
      await Promise.resolve(onMerge({ swimmerIds: localSelected, ...previewVals }))
      setConfirmLoading(false)
      setShowConfirmModal(false)
      form.resetFields()
      onClose()
    } catch (err) {
      console.log(err)
      setConfirmLoading(false)
      message.error("Uh oh, something went wrong merging swimmers.")
    }
  }

  const steps = [
    {
      title: "Select Swimmers to Merge",
    },
    {
      title: "Confirm Merged Swimmer's Details",
    },
  ]

  // allow clicking steps but enforce selection requirement to view step 2
  const onStepChange = (next: number) => {
    if (next === 1 && localSelected.length <= 1) {
      message.warning("Select two or more swimmers to proceed.")
      return
    }
    setCurrent(next)
  }

  // modal footer varies by step
  const footerButtons = current === 0 ? [
    <Button key="cancel" type="text" onClick={() => { form.resetFields(); onClose() }}>Cancel</Button>,
    <Button key="next" type="primary" onClick={goNext} disabled={localSelected.length <= 1}>Next <FontAwesomeIcon icon={faChevronRight} /></Button>,
  ] : [
    <Button key="cancel" type="text" onClick={() => { form.resetFields(); onClose() }}>Cancel</Button>,
    <Button key="back" icon={<LeftOutlined />} onClick={goBack}>Back</Button>,
    <Button key="merge" type="primary" onClick={onPrepareMerge} disabled={localSelected.length <= 1}>Merge Swimmers Now</Button>,
  ]

  // compute selected swimmers for display
  const selectedSwimmersList = usersSwimmers
    .filter((u: any) => u?.swimmer?.swimmerId && localSelected.includes(u.swimmer.swimmerId))
    .map((u: any) => ({
      id: u.swimmer.swimmerId,
      name: `${u.swimmer.firstName}${u.swimmer.middleInitials ? " " + u.swimmer.middleInitials : ""} ${u.swimmer.lastName}`,
      dob: u.swimmer.dateOfBirth ? moment(u.swimmer.dateOfBirth).format("YYYY-MM-DD") : "",
    }))

  return (
    <>
      <Modal
        title="Merge Duplicate Swimmer Records"
        open={visible}
        centered
        mask={{ closable: false }}
        onCancel={() => { form.resetFields(); onClose() }}
        footer={footerButtons}
      >

        <Steps
          current={current}
          onChange={onStepChange}
          style={{ marginBottom: 16 }}
          items={steps.map((s) => ({ key: s.title, title: s.title }))}
        />

        {current === 0 && (
          <>
            <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 12 }}>
              {usersSwimmers.map((s) => {
                const id = s?.swimmer?.swimmerId
                return (
                  <div key={id} style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={id !== undefined && localSelected.includes(id)}
                      onChange={() => toggleSelection(id)}
                      aria-label={`select-swimmer-${id}`}
                    />
                    <div>
                      <strong>{s.swimmer.firstName} {s.swimmer.middleInitials ?? ""} {s.swimmer.lastName}</strong>
                      <div style={{ fontSize: 12, color: "var(--darkgrey)" }}>{s.swimmer.dateOfBirth ? moment(s.swimmer.dateOfBirth).format("YYYY-MM-DD") : ""}</div>
                    </div>
                  </div>
                )
              })}
            </div>
            <Divider />
            <p style={{ color: "var(--darkgrey)" }}>Select two or more swimmers above to merge.</p>
          </>
        )}

        {current === 1 && (
          <>
            {/* Selected swimmers summary at top of Step 2 */}
            <div style={{ marginBottom: 12 }}>
              <strong>Selected swimmers will merge into one swimmer, confirm the swimmer's new details below:</strong>
              <div style={{ marginTop: 8 }}>
                {selectedSwimmersList.map((s) => (
                  <div key={s.id} style={{ fontSize: 13, color: "var(--darkgrey)", marginBottom: 6 }}>
                    {s.name} — {s.dob}
                  </div>
                ))}
              </div>
            </div>

            <Form form={form} layout="vertical" preserve={false} initialValues={{ firstName: "", middleInitial: "", lastName: "", gender: "", birthdate: null }}>
              <Form.Item name="firstName" rules={[{ required: true }]}><Input placeholder="First name..." /></Form.Item>
              <Form.Item name="middleInitial" rules={[{ pattern: /^[a-zA-Z]?$/, message: "Single letter" }]}><Input placeholder="Middle Initial" maxLength={1} /></Form.Item>
              <Form.Item name="lastName" rules={[{ required: true }]}><Input placeholder="Last name..." /></Form.Item>
              <Form.Item name="birthdate" label="Date of Birth" rules={[{ required: true }]}><DOBPicker value={form.getFieldValue("birthdate") ?? null} onChange={(d) => form.setFieldValue("birthdate", d)} /></Form.Item>
              <Form.Item name="gender" label="Sex" rules={[{ required: true }]}><Radio.Group><Radio value="Boy">Boy</Radio><Radio value="Girl">Girl</Radio></Radio.Group></Form.Item>
            </Form>
            <Divider />
            <p style={{ color: "var(--darkgrey)" }}>All historical data from both accounts will be merged and preserved in the new account.</p>
          </>
        )}
      </Modal>

      {/* Confirmation modal before final merge */}
      <Modal
        title="Confirm Merge"
        open={showConfirmModal}
        centered
        mask={{ closable: false }}
        onCancel={() => setShowConfirmModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowConfirmModal(false)}>Cancel</Button>,
          <Button key="confirm" danger type="primary" loading={confirmLoading} onClick={handleConfirmMerge}>Yes, merge swimmers</Button>,
        ]}
        wrapClassName="confirm-merge-modal"                // ensure confirm modal sits above the parent modal
        getContainer={() => document.body}
      >
        <p><strong>Old swimmers</strong></p>
        <div style={{ marginBottom: 12 }}>
          {selectedSwimmersList.map((s) => (
            <div key={s.id} style={{ fontSize: 13, color: "var(--darkgrey)", marginBottom: 6 }}>
              {s.name} — {s.dob}
            </div>
          ))}
        </div>

        <Divider />

        <p><strong>New swimmer</strong></p>
        <div style={{ marginBottom: 12, color: "var(--darkgrey)" }}>
          <div><strong>Name:</strong> {previewVals ? `${previewVals.firstName}${previewVals.middleInitial ? " " + previewVals.middleInitial : ""} ${previewVals.lastName}` : "-"}</div>
          <div><strong>Sex:</strong> {previewVals ? previewVals.gender : "-"}</div>
          <div><strong>Birthdate:</strong> {previewVals && previewVals.birthdate ? previewVals.birthdate.format("YYYY-MM-DD") : "-"}</div>
        </div>

        <p style={{ color: "var(--darkgrey)" }}>
          Are you sure you want to merge the old swimmers into one new swimmer? This action cannot be undone.
        </p>
      </Modal>
    </>
  )
}